import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import FileDropZone from 'components/shared/FileDropZone';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import StandardModal, { Actions, ColorThemeName, ModalContent } from 'components/shared/StandardModal';
import Upload from 'hooks/useDirectUpload/Upload';
import useDirectUpload from 'hooks/useDirectUpload/useDirectUpload';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { adminClientsQueryKeys, adminIndexClients } from 'services/api/admin/clients';
import { createDenialListImport, DenialListImport } from 'services/api/admin/denialListImports';
import { colors } from 'styles/theme/colors';
import ClipIcon from 'svg/AttachFileIcon';
import TrashIcon from 'svg/TrashIcon';

// The five headers the backend requires, surfaced to the implementation team up front.
const REQUIRED_HEADERS = ['Patient Name', 'Date of Birth', 'Gender', 'Reason', 'Date Added'];

// Mirror the backend's authoritative cap (Admin::DenialListImport::Create::MAX_FILE_SIZE). 10 MB
// comfortably fits a ~10k-row list even with long reasons.
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
// CSV only. The '.csv' extension entry still accepts files the OS mislabels (e.g. Windows reports a
// .csv's MIME as application/vnd.ms-excel) via extension match, but a real .xls/.xlsx is rejected.
const CSV_TYPES = { 'text/csv': ['.csv'] };

type ClientOption = { id: string; name: string };

type Props = {
  onClose: () => void;
  onUploaded: (denialListImport: DenialListImport) => void;
};

export default function UploadDenialListModal({ onClose, onUploaded }: Props) {
  const [client, setClient] = useState<ClientOption | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Synchronous double-submit guard. `submitting` state (and the button's disabled/loading props)
  // update asynchronously, so two rapid clicks both read the stale value and fire two POSTs —
  // creating a duplicate import that then loses the one-active-list-per-client race and strands the
  // UI on "processing" (OLIO-2462). A ref flips synchronously, so the second click is a no-op.
  const submittingRef = useRef(false);

  // Same eligible-clients source as the "Add New List" modal: only clients with the denial-list
  // product access on AND no active list (the `denial_list_eligible` filter), so Upload can't target
  // a client that already has one. Shares the query cache with that modal via the same key.
  const clientOptions = useInfiniteOptions<ClientOption>({
    queryKey: adminClientsQueryKeys.denialListEligible,
    fetchFn: (params, signal) => adminIndexClients(params, signal),
    params: { sortBy: 'name asc', denialListEligible: true },
  });

  // Direct-to-S3 upload; the completed Upload carries the ActiveStorage signed id we send on.
  const { uploads, uploadFiles, removeUpload } = useDirectUpload([]);
  const upload: Upload | undefined = uploads[0];
  const fileReady = !!upload?.isComplete && !!upload.signedId;

  const canUpload = !!client && fileReady && !submitting;

  const handleFiles = (files: File[]) => {
    setError(null);
    const file = files[0];
    if (!file) return;

    // Reject oversize files up front so we never upload them to S3 or enable the Upload button
    // (the backend enforces the same cap authoritatively).
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError('The file is larger than 10 MB.');
      return;
    }

    uploadFiles([file]);
  };

  const handleUpload = async () => {
    if (submittingRef.current || !canUpload || !upload) return;
    submittingRef.current = true;

    setSubmitting(true);
    setError(null);
    try {
      const created = await createDenialListImport({
        clientId: client!.id,
        signedId: upload.signedId!,
        filename: upload.filename,
      });
      onUploaded(created);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'The denial list could not be uploaded. Please try again.');
      setSubmitting(false);
      // Re-open the gate so the user can retry after a failure (success unmounts the modal).
      submittingRef.current = false;
    }
  };

  const dropzoneIcon = useMemo(() => <ClipIcon height={16} />, []);

  return (
    <StandardModal title='Upload New Denial List' colorTheme={ColorThemeName.Primary} onCancel={onClose}>
      <ModalContent>
        <Intro>
          Attach a .csv file to upload an initial denial list. The file requires the following column headers:
          <HeaderList>
            {REQUIRED_HEADERS.map((header) => (
              <li key={header}>{header}</li>
            ))}
          </HeaderList>
        </Intro>

        <Field>
          <FieldLabel>Client</FieldLabel>
          <Select<ClientOption>
            {...clientOptions}
            value={client}
            onChange={setClient}
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => option.name}
            placeholder='Client'
          />
        </Field>

        <FileDropZone
          onFilesSelected={handleFiles}
          allowedTypes={CSV_TYPES}
          maxFiles={1}
          currentFileCount={upload ? 1 : 0}
          noClick={!!upload}
          noDrag={!!upload}>
          {(dropzone) => (
            <DropArea {...dropzone.getRootProps()} $active={dropzone.isDragActive} $disabled={!!upload}>
              <input {...dropzone.getInputProps()} />
              {dropzoneIcon}
              <DropLabel>
                Drag and drop your file(s) here, or&nbsp;
                <BrowseLink aria-label='launch file browser'>browse your files</BrowseLink>
              </DropLabel>
            </DropArea>
          )}
        </FileDropZone>

        {upload && (
          <AttachedFile>
            <FileName>{upload.filename}</FileName>
            <DeleteButton aria-label='remove file' onClick={() => removeUpload(upload.id)}>
              <TrashIcon width={16} height={16} color={colors.black50} />
            </DeleteButton>
          </AttachedFile>
        )}

        {error && <ErrorText>{error}</ErrorText>}
      </ModalContent>

      <Actions>
        <Button variant='ghost' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='primary' disabled={!canUpload} loading={submitting} onClick={handleUpload}>
          Upload
        </Button>
      </Actions>
    </StandardModal>
  );
}

const Intro = styled.div`
  font-size: 14px;
  color: ${colors.black};
  line-height: 1.5;
  text-align: left;
`;

const HeaderList = styled.ul`
  margin: 4px 0 0;
  padding-left: 24px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 20px;
  text-align: left;
`;

const FieldLabel = styled.label`
  font-size: 12px;
  color: ${colors.black75};
`;

const DropArea = styled.div<{ $active: boolean; $disabled: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  padding: 32px 16px;
  border: 1px dashed ${({ $active }) => ($active ? colors.primaryBlue : colors.black25)};
  border-radius: 8px;
  background: ${({ $active }) => ($active ? colors.primaryBlue05 : 'transparent')};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  text-align: center;
`;

const DropLabel = styled.span`
  font-size: 14px;
  color: ${colors.black75};
`;

const BrowseLink = styled.span`
  color: ${colors.primaryBlue};
  text-decoration: underline;
`;

const AttachedFile = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 12px;
  padding: 12px 16px;
  border: 1px solid ${colors.black15};
  border-radius: 8px;
`;

const FileName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.primaryBlue};
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
`;

const ErrorText = styled.p`
  margin: 12px 0 0;
  font-size: 13px;
  color: ${colors.accentRed};
`;
